import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFamilyStore, FamilyMember } from '../store/familyStore';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import ImageCropper from '../components/ImageCropper';
import NavBar from '../components/NavBar';
import { CoreNavRail } from '../components/CoreNavRail';
import ConfirmDialog from '../components/ConfirmDialog';
import ColorPicker from '../components/ColorPicker';
import { getDeviceType } from '../utils/deviceDetection';
import { getApiUrl, getApiKey } from '../utils/apiConfig';
import { useToastStore } from '../store/toastStore';
import LoadingSpinner from '../components/LoadingSpinner';
import { Icon } from '../utils/icons';

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-800',
  parent: 'bg-blue-100 text-blue-800',
  child: 'bg-green-100 text-green-800',
  adult: 'bg-gray-100 text-gray-800',
};

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  parent: 'Parent',
  child: 'Child',
  adult: 'Adult',
};

export default function Family() {
  const { family, invitations, isLoading, error, fetchFamily, createFamily, updateFamily, addMember, updateMember, removeMember, createInvitation, listInvitations, resendInvitation, cancelInvitation, clearError } = useFamilyStore();
  const { isAuthenticated, signOut, signOutFromAllDevices } = useAuthStore();
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showSignOutMenu, setShowSignOutMenu] = useState(false);
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const { showToast } = useToastStore();
  const navigate = useNavigate();
  const deviceType = getDeviceType();
  const isMobileOrTablet = deviceType === 'mobile' || deviceType === 'tablet';
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showEditMemberForm, setShowEditMemberForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editingMemberName, setEditingMemberName] = useState('');
  const [editingMemberColor, setEditingMemberColor] = useState('#3b82f6');
  const [editingMemberAvatar, setEditingMemberAvatar] = useState('');
  const [showEditMemberPhoto, setShowEditMemberPhoto] = useState<string | null>(null); // memberId of member being edited
  const [familyName, setFamilyName] = useState('');
  const [familyLocationCity, setFamilyLocationCity] = useState('');
  const [familyLocationLat, setFamilyLocationLat] = useState<string>('');
  const [familyLocationLng, setFamilyLocationLng] = useState<string>('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberName, setMemberName] = useState('');
  const [memberRole, setMemberRole] = useState<'parent' | 'child' | 'adult'>('parent');
  const [memberColor, setMemberColor] = useState('#3b82f6');
  const [memberAvatar, setMemberAvatar] = useState('');
  const [resendingInvitations, setResendingInvitations] = useState<Set<string>>(new Set());
  const [sentInvitations, setSentInvitations] = useState<Set<string>>(new Set());
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState('');
  const [croppingForFamily, setCroppingForFamily] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isSavingFamily, setIsSavingFamily] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [isInvitingMember, setIsInvitingMember] = useState(false);
  const [showRemoveMemberConfirm, setShowRemoveMemberConfirm] = useState(false);
  const [pendingRemoveMemberId, setPendingRemoveMemberId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const editMemberFileInputRef = useRef<HTMLInputElement>(null);
  const familyPhotoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // First check: if not authenticated, redirect immediately
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    
    // Second check: if authenticated, fetch family and invitations
    if (isAuthenticated) {
      fetchFamily().catch((error) => {
        // If fetch fails (e.g., token expired), check auth state and redirect to login
        console.error('Failed to fetch family:', error);
        const authState = useAuthStore.getState();
        // If authentication error or tokens are missing/invalid, redirect to login
        if (error.message?.includes('Authentication failed') || !authState.isAuthenticated || !authState.idToken) {
          navigate('/login', { replace: true });
        }
      });
      
      // Load pending invitations if user can manage family (will be checked after family loads)
    }
  }, [isAuthenticated, fetchFamily, navigate]);

  // Load invitations when family is available and user can manage
  useEffect(() => {
    if (family) {
      const currentUserId = getCurrentUserId();
      const currentUserMember = family.members.find(m => m.userId === currentUserId);
      const canManageFamily = currentUserMember && (currentUserMember.role === 'owner' || currentUserMember.role === 'parent');
      if (canManageFamily) {
        listInvitations('pending').catch((error) => {
          console.error('Error loading invitations:', error);
          showToast(error instanceof Error ? error.message : 'Failed to load invitations', 'error');
        });
      }
    }
  }, [family, listInvitations, showToast]);

  useEffect(() => {
    if (family) {
      setFamilyName(family.name);
      setFamilyLocationCity(family.location?.city || '');
      setFamilyLocationLat(family.location?.latitude?.toString() || '');
      setFamilyLocationLng(family.location?.longitude?.toString() || '');
    }
  }, [family]);

  const handleCreateFamily = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSavingFamily) return;
    setIsSavingFamily(true);
    try {
      await createFamily(familyName);
      setShowCreateForm(false);
      setFamilyName('');
      // Redirect to calendar after creating family
      navigate('/', { replace: true });
      showToast('Family created', 'success');
    } catch (err) {
      console.error('Error creating family:', err);
      showToast(err instanceof Error ? err.message : 'Failed to create family', 'error');
    } finally {
      setIsSavingFamily(false);
    }
  };

  const handleUpdateFamily = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSavingFamily) return;
    setIsSavingFamily(true);
    try {
      const updates: { name?: string; location?: { city?: string; latitude?: number; longitude?: number } } = {};
      
      if (familyName !== family?.name) {
        updates.name = familyName;
      }

      // Build location object
      const location: { city?: string; latitude?: number; longitude?: number } = {};
      if (familyLocationCity !== (family?.location?.city || '')) {
        location.city = familyLocationCity.trim() || undefined;
      }
      if (familyLocationLat !== (family?.location?.latitude?.toString() || '')) {
        location.latitude = familyLocationLat.trim() ? parseFloat(familyLocationLat) : undefined;
      }
      if (familyLocationLng !== (family?.location?.longitude?.toString() || '')) {
        location.longitude = familyLocationLng.trim() ? parseFloat(familyLocationLng) : undefined;
      }

      // Only include location if it has at least one field
      if (location.city !== undefined || location.latitude !== undefined || location.longitude !== undefined) {
        updates.location = location;
      }

      if (Object.keys(updates).length > 0) {
        await updateFamily(updates);
      }
      setShowEditForm(false);
      if (Object.keys(updates).length > 0) {
        showToast('Family updated', 'success');
      }
    } catch (err) {
      console.error('Error updating family:', err);
      showToast(err instanceof Error ? err.message : 'Failed to update family', 'error');
    } finally {
      setIsSavingFamily(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_SIZE) {
      showToast('File size exceeds 5MB. Please choose a smaller image.', 'error');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Invalid file type. Please choose a JPEG, PNG, GIF, or WebP image.', 'error');
      return;
    }

    // Determine which context we're in based on which input was used
    const isFamilyPhoto = e.target === familyPhotoInputRef.current;
    const isEditMemberForm = e.target === editMemberFileInputRef.current;
    if (isFamilyPhoto) {
      setCroppingForFamily(true);
      setShowEditMemberPhoto(null);
    } else {
      setCroppingForFamily(false);
    }
    if (isEditMemberForm && editingMemberId) {
      setShowEditMemberPhoto(editingMemberId);
    }

    // Read file and show cropper
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageUrl = event.target?.result as string;
      setImageToCrop(imageUrl);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleEditMemberPhoto = (memberId: string) => {
    setShowEditMemberPhoto(memberId);
    editFileInputRef.current?.click();
  };

  const handleEditMember = (member: FamilyMember) => {
    setEditingMemberId(member.userId || '');
    setEditingMemberName(member.name);
    setEditingMemberColor(member.color);
    setEditingMemberAvatar(member.avatar || '');
    setShowEditMemberForm(true);
  };

  const handleEditMemberPhotoInForm = () => {
    if (editingMemberId) {
      setShowEditMemberPhoto(editingMemberId);
      editMemberFileInputRef.current?.click();
    }
  };

  const handleEditMemberSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMemberId) return;
    if (isUpdatingMember) return;
    setIsUpdatingMember(true);

    try {
      const updates: { name?: string; color?: string; avatar?: string } = {
        name: editingMemberName,
        color: editingMemberColor,
      };
      
      // Only include avatar if it was changed
      if (editingMemberAvatar && editingMemberAvatar !== family?.members.find(m => m.userId === editingMemberId)?.avatar) {
        updates.avatar = editingMemberAvatar;
      }

      await updateMember(editingMemberId, updates);
      setShowEditMemberForm(false);
      setEditingMemberId(null);
      setEditingMemberName('');
      setEditingMemberColor('#3b82f6');
      setEditingMemberAvatar('');
      showToast('Member updated', 'success');
    } catch (err) {
      console.error('Error updating member:', err);
      showToast(err instanceof Error ? err.message : 'Failed to update member', 'error');
    } finally {
      setIsUpdatingMember(false);
    }
  };

  const handleCropComplete = async (croppedImageDataUrl: string) => {
    setShowCropper(false);
    setUploading(true);

    try {
      // Convert data URL to blob
      const response = await fetch(croppedImageDataUrl);
      const blob = await response.blob();

      // Check file size - if still too large, compress further
      const MAX_FILE_SIZE = 500 * 1024; // 500KB max
      let finalBlob = blob;
      
      if (blob.size > MAX_FILE_SIZE) {
        // Further compress the image
        const img = new Image();
        img.src = croppedImageDataUrl;
        await new Promise((resolve) => {
          img.onload = resolve;
        });

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not create canvas context');

        // Reduce size further if needed
        let targetSize = 300; // Start with 300x300
        let quality = 0.75;
        
        canvas.width = targetSize;
        canvas.height = targetSize;
        
        // Note: We store as square, display as circle using CSS border-radius
        // No circular clipping needed - just draw the square image
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, targetSize, targetSize);
        
        // Try different quality levels until we get under the size limit
        let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        let compressedBlob = await (await fetch(compressedDataUrl)).blob();
        
        while (compressedBlob.size > MAX_FILE_SIZE && quality > 0.5) {
          quality -= 0.1;
          compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          compressedBlob = await (await fetch(compressedDataUrl)).blob();
        }
        
        finalBlob = compressedBlob;
      }

      // Get upload URL from API
      const API_URL = getApiUrl();
      const API_KEY = getApiKey();
      const { idToken } = useAuthStore.getState();

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (API_KEY) {
        headers['X-Api-Key'] = API_KEY;
      }
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      // Get presigned URL (family avatar or member photo)
      const uploadUrlResponse = await fetch(`${API_URL}/upload/url`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          fileName: croppingForFamily ? 'family-photo.jpg' : 'member-photo.jpg',
          fileType: 'image/jpeg',
          fileSize: finalBlob.size,
          ...(croppingForFamily && { purpose: 'familyAvatar' }),
        }),
      });

      if (!uploadUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, fileUrl } = await uploadUrlResponse.json();

      // Upload to S3 using presigned URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: finalBlob,
        headers: {
          'Content-Type': 'image/jpeg',
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image');
      }

      if (croppingForFamily) {
        await updateFamily({ avatar: fileUrl });
        setCroppingForFamily(false);
        setImageToCrop('');
        showToast('Family photo updated', 'success');
        setUploading(false);
        return;
      }

      // If editing existing member photo (either from edit form or direct photo edit)
      if (showEditMemberPhoto) {
        // If we're in the edit form, update the form state
        if (showEditMemberForm && editingMemberId === showEditMemberPhoto) {
          setEditingMemberAvatar(fileUrl);
        } else {
          // Direct photo edit - update member immediately
          await updateMember(showEditMemberPhoto, { avatar: fileUrl });
          setShowEditMemberPhoto(null);
          showToast('Photo updated', 'success');
        }
        setShowEditMemberPhoto(null);
      } else {
        // Set the public URL for new member
        setMemberAvatar(fileUrl);
        showToast('Photo ready to save', 'success');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      showToast(error?.message || 'Failed to upload image', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate email is provided for non-child members
    if (memberRole !== 'child' && !memberEmail.trim()) {
      showToast('Email is required for parents and adults', 'error');
      return;
    }
    if (isAddingMember) return;
    setIsAddingMember(true);
    try {
      // For parent/adult with email, try creating invitation first
      // The backend will handle creating invitation if user doesn't exist
      if (memberRole !== 'child' && memberEmail.trim()) {
        try {
          const result = await createInvitation(memberEmail.trim(), memberName, memberRole, memberColor);
          if (result.action === 'invited') {
            // Invitation created successfully
            setShowAddMemberForm(false);
            setMemberEmail('');
            setMemberName('');
            setMemberRole('parent');
            setMemberColor('#3b82f6');
            setMemberAvatar('');
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            showToast('Invitation sent', 'success');
            return;
          }
          // If action is 'added', user was added immediately, continue to refresh
        } catch (inviteErr: any) {
          // If invitation fails (e.g., user already exists), try direct add
          if (inviteErr.message?.includes('already exists') || inviteErr.message?.includes('already a member')) {
            // Fall through to direct addMember
          } else {
            throw inviteErr;
          }
        }
      }
      
      // Direct add (for children or if invitation didn't work)
      await addMember(memberEmail || undefined, memberName, memberRole, memberColor, memberAvatar || undefined);
      setShowAddMemberForm(false);
      setMemberEmail('');
      setMemberName('');
      setMemberRole('parent');
      setMemberColor('#3b82f6');
      setMemberAvatar('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      showToast('Member added', 'success');
    } catch (err) {
      console.error('Error adding member:', err);
      showToast(err instanceof Error ? err.message : 'Failed to add member', 'error');
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleCreateInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberEmail.trim()) {
      showToast('Email is required', 'error');
      return;
    }
    if (memberRole === 'child') {
      showToast('Children cannot be invited. Add them directly.', 'error');
      return;
    }
    if (isInvitingMember) return;
    setIsInvitingMember(true);
    try {
      await createInvitation(memberEmail.trim(), memberName, memberRole, memberColor);
      setShowInviteForm(false);
      setMemberEmail('');
      setMemberName('');
      setMemberRole('parent');
      setMemberColor('#3b82f6');
      showToast('Invitation sent', 'success');
    } catch (err) {
      console.error('Error creating invitation:', err);
      showToast(err instanceof Error ? err.message : 'Failed to send invitation', 'error');
    } finally {
      setIsInvitingMember(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    // Optimistic update: show "Sending" state immediately
    setResendingInvitations(prev => new Set(prev).add(invitationId));
    
    try {
      await resendInvitation(invitationId);
      // Show "Sent" state
      setResendingInvitations(prev => {
        const next = new Set(prev);
        next.delete(invitationId);
        return next;
      });
      setSentInvitations(prev => new Set(prev).add(invitationId));
      
      // After 2 seconds, revert to "Resend"
      setTimeout(() => {
        setSentInvitations(prev => {
          const next = new Set(prev);
          next.delete(invitationId);
          return next;
        });
      }, 2000);
      showToast('Invitation resent', 'success');
    } catch (err) {
      // On error, revert to "Resend" state
      setResendingInvitations(prev => {
        const next = new Set(prev);
        next.delete(invitationId);
        return next;
      });
      console.error('Error resending invitation:', err);
      showToast(err instanceof Error ? err.message : 'Failed to resend invitation', 'error');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId);
      showToast('Invitation canceled', 'success');
    } catch (err) {
      console.error('Error canceling invitation:', err);
      showToast(err instanceof Error ? err.message : 'Failed to cancel invitation', 'error');
    }
  };

  const handleRemoveMember = (memberId: string) => {
    setPendingRemoveMemberId(memberId);
    setShowRemoveMemberConfirm(true);
  };

  const handleConfirmRemoveMember = async () => {
    if (!pendingRemoveMemberId) return;
    if (isRemovingMember) return;
    setIsRemovingMember(true);

    try {
      await removeMember(pendingRemoveMemberId);
      showToast('Member removed', 'success');
      setShowRemoveMemberConfirm(false);
      setPendingRemoveMemberId(null);
    } catch (err) {
      console.error('Error removing member:', err);
      showToast(err instanceof Error ? err.message : 'Failed to remove member', 'error');
      setShowRemoveMemberConfirm(false);
      setPendingRemoveMemberId(null);
    } finally {
      setIsRemovingMember(false);
    }
  };

  const handleCancelRemoveMember = () => {
    setShowRemoveMemberConfirm(false);
    setPendingRemoveMemberId(null);
  };

  // Check if current user can manage family (owner or parent)
  const getCurrentUserId = (): string | null => {
    const { idToken } = useAuthStore.getState();
    if (!idToken) return null;
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return payload.sub;
    } catch {
      return null;
    }
  };

  const currentUserId = getCurrentUserId();
  const currentUserMember = family?.members.find(m => m.userId === currentUserId);
  const canManageFamily = currentUserMember && (currentUserMember.role === 'owner' || currentUserMember.role === 'parent');
  
  // Check if user can edit a specific member's photo
  const canEditMemberPhoto = (member: any): boolean => {
    if (!currentUserMember || !member.userId) return false;
    
    const isOwner = currentUserMember.role === 'owner';
    const isParent = currentUserMember.role === 'parent' || currentUserMember.role === 'owner';
    const isTargetChild = member.role === 'child';
    const isEditingSelf = member.userId === currentUserId;
    
    // Owner can edit anyone
    if (isOwner) return true;
    
    // Parent can edit children
    if (isParent && isTargetChild) return true;
    
    // Parent can edit themselves (but not other parents/adults)
    if (isParent && isEditingSelf) return true;
    
    // Others can only edit themselves
    if (isEditingSelf) return true;
    
    return false;
  };

  // Early return if not authenticated - redirect to login
  if (!isAuthenticated) {
    // Double-check tokens are actually present
    const authState = useAuthStore.getState();
    if (!authState.idToken || !authState.accessToken || !authState.refreshToken) {
      navigate('/login', { replace: true });
      return null;
    }
    // If isAuthenticated is false but we have tokens, something is wrong - redirect anyway
    navigate('/login', { replace: true });
    return null;
  }
  
  // Additional check: verify tokens are actually present
  const authState = useAuthStore.getState();
  if (!authState.idToken || !authState.accessToken) {
    navigate('/login', { replace: true });
    return null;
  }

  if (isLoading && !family) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${tokens.bg.gradient} transition-colors duration-300`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${tokens.border.accent} mx-auto mb-4`}></div>
          <p className={tokens.text.muted}>Loading family...</p>
        </div>
      </div>
    );
  }

  if (!family && !showCreateForm) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${tokens.bg.gradient} p-4 transition-colors duration-300`}>
        <div className={`max-w-md w-full ${tokens.card.bg} rounded-landing ${tokens.card.shadow} p-8`}>
          <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-4 font-serif`}>Create Your Family</h1>
          <p className={`text-sm ${tokens.text.muted} mb-6`}>Get started by creating a family group to share calendars and manage activities together.</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className={`w-full ${tokens.button.primary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target mb-3`}
          >
            Create Family
          </button>
          <button
            onClick={() => {
              signOut();
              navigate('/login');
            }}
            className={`w-full ${tokens.button.secondary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target flex items-center justify-center gap-2`}
          >
            <Icon name="sign-out" size="sm" className={tokens.text.primary} />
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} transition-colors duration-300 relative pr-20`}>
      <NavBar className={`${tokens.card.bg} ${tokens.card.shadow} rounded-landing`} containerClassName="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between w-full gap-3 sm:gap-4 min-w-0">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-shrink-0">
            <Link
              to="/"
              className={`text-lg sm:text-xl font-bold ${tokens.text.primary} hover:opacity-80 transition-colors truncate font-serif`}
            >
              Runow
            </Link>
            <div className={`h-6 w-px ${tokens.border.light} flex-shrink-0`} />
            <Link
              to="/"
              className={`px-3 sm:px-4 h-12 ${tokens.card.bg} ${isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]'} ${tokens.text.primary} font-semibold rounded-landing transition-colors ${tokens.border.light} border flex items-center justify-center flex-shrink-0 text-xs sm:text-sm`}
            >
              Calendar
            </Link>
            <Link
              to="/family"
              className={`px-3 sm:px-4 h-12 ${tokens.button.primary} font-semibold rounded-landing transition-colors flex items-center justify-center flex-shrink-0 text-xs sm:text-sm`}
            >
              Family
            </Link>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0 relative">
            <div className="relative">
              <button
                onClick={() => setShowSignOutMenu(!showSignOutMenu)}
                className={`px-4 sm:px-6 h-12 ${tokens.button.secondary} ${tokens.text.primary} font-semibold rounded-landing transition-colors flex items-center justify-center flex-shrink-0 text-xs sm:text-sm`}
              >
                Sign Out
              </button>
              {showSignOutMenu && (
                <div className={`absolute right-0 top-full mt-2 ${tokens.card.bg} ${tokens.border.light} border rounded-landing ${tokens.card.shadow} min-w-[200px] z-50`}>
                  <button
                    onClick={() => {
                      signOut();
                      navigate('/login');
                      setShowSignOutMenu(false);
                    }}
                    className={`w-full ${tokens.text.primary} ${tokens.card.bg} hover:bg-opacity-80 px-4 py-3 text-left transition-colors min-h-[48px] flex items-center`}
                  >
                    Sign Out (This Device)
                  </button>
                  <button
                    onClick={() => {
                      setShowSignOutMenu(false);
                      setShowSignOutConfirm(true);
                    }}
                    className={`w-full ${tokens.text.primary} ${tokens.card.bg} hover:bg-opacity-80 px-4 py-3 text-left transition-colors min-h-[48px] flex items-center border-t ${tokens.border.light}`}
                  >
                    Sign Out (All Devices)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </NavBar>

      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className={`${tokens.card.bg} rounded-landing ${tokens.card.shadow} p-6 mb-8`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 mb-4">
                  {family?.name || 'Family'}
                </h1>
                {family && (
                  <p className="text-sm text-gray-600">
                    Created {new Date(family.createdAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              {family && canManageFamily && (
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold bg-gray-300 overflow-hidden"
                      style={family.avatar ? undefined : { backgroundColor: '#3b82f6' }}
                    >
                      {!family.avatar && (family.name?.charAt(0).toUpperCase() || 'F')}
                      {family.avatar && (
                        <img
                          src={family.avatar}
                          alt="Family"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-gray-700">Family photo</span>
                    <input
                      ref={familyPhotoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => familyPhotoInputRef.current?.click()}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium touch-target"
                      >
                        {family.avatar ? 'Change' : 'Add photo'}
                      </button>
                      {family.avatar && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await updateFamily({ avatar: '' });
                              showToast('Family photo removed', 'success');
                            } catch (err) {
                              showToast('Failed to remove photo', 'error');
                            }
                          }}
                          className="text-sm text-gray-600 hover:text-gray-800 font-medium touch-target"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {family && canManageFamily && (
              <div className="flex gap-4">
                <button
                  onClick={() => setShowEditForm(true)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-landing transition-colors touch-target"
                >
                  Edit Family
                </button>
                <button
                  onClick={() => setShowAddMemberForm(true)}
                  className={`px-4 py-2 ${tokens.button.primary} text-white font-semibold rounded-landing transition-colors touch-target`}
                >
                  Add Member
                </button>
                <button
                  onClick={() => setShowInviteForm(true)}
                  className={`px-4 py-2 ${tokens.button.primary} font-semibold rounded-landing transition-colors touch-target`}
                >
                  Invite Member
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-landing mb-6">
            <div className="flex justify-between items-center">
              <span>{error}</span>
              <button
                onClick={clearError}
                className="text-red-700 hover:text-red-900 font-bold"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Edit Member Form - Show at top when editing */}
        {showEditMemberForm && editingMemberId && (
          <div className="bg-white rounded-landing shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Edit Member</h2>
            <form onSubmit={handleEditMemberSubmit} className="space-y-4">
              <div>
                <label htmlFor="editMemberName" className="block text-xs font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="editMemberName"
                  value={editingMemberName}
                  onChange={(e) => setEditingMemberName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                  placeholder="Member name"
                />
              </div>
              <div>
                {isMobileOrTablet ? (
                  <ColorPicker
                    value={editingMemberColor}
                    onChange={setEditingMemberColor}
                    label="Color"
                  />
                ) : (
                  <>
                    <label htmlFor="editMemberColor" className="block text-sm font-medium text-gray-700 mb-2">
                      Color
                    </label>
                    <input
                      type="color"
                      id="editMemberColor"
                      value={editingMemberColor}
                      onChange={(e) => setEditingMemberColor(e.target.value)}
                      className="w-full h-12 border border-gray-300 rounded-landing cursor-pointer touch-target"
                    />
                  </>
                )}
              </div>
              <div>
                <label htmlFor="editMemberAvatar" className="block text-xs font-medium text-gray-700 mb-2">
                  Photo (optional)
                </label>
                <input
                  ref={editMemberFileInputRef}
                  type="file"
                  id="editMemberAvatar"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={handleEditMemberPhotoInForm}
                    disabled={uploading}
                    className="px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-landing transition-colors touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
                  >
                    {uploading ? 'Uploading...' : editingMemberAvatar ? 'Change Photo' : 'Choose Photo'}
                  </button>
                  {editingMemberAvatar && (
                    <div className="relative">
                      <img
                        src={editingMemberAvatar}
                        alt="Preview"
                        className="w-16 h-16 rounded-landing-full object-cover border-2 border-gray-300"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setEditingMemberAvatar('')}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-landing-full flex items-center justify-center text-xs font-bold hover:bg-red-600 touch-target"
                        aria-label="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500">Max file size: 5MB. Supported formats: JPEG, PNG, GIF, WebP</p>
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className={`flex-1 ${tokens.button.primary} text-white font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
                  disabled={isUpdatingMember}
                >
                  {isUpdatingMember ? <LoadingSpinner size="sm" label="Saving member" /> : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditMemberForm(false);
                    setEditingMemberId(null);
                    setEditingMemberName('');
                    setEditingMemberColor('#3b82f6');
                    setEditingMemberAvatar('');
                    if (editMemberFileInputRef.current) {
                      editMemberFileInputRef.current.value = '';
                    }
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-landing transition-colors touch-target"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Create Family Form */}
        {showCreateForm && (
          <div className="bg-white rounded-landing shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Create Family</h2>
            <form onSubmit={handleCreateFamily} className="space-y-4">
              <div>
                <label htmlFor="familyName" className="block text-xs font-medium text-gray-700 mb-2">
                  Family Name
                </label>
                <input
                  type="text"
                  id="familyName"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                  placeholder="Enter family name"
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className={`flex-1 ${tokens.button.primary} text-white font-semibold py-3 px-6 rounded-landing transition-colors touch-target flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
                  disabled={isSavingFamily}
                >
                  {isSavingFamily ? <LoadingSpinner size="sm" label="Creating family" /> : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setFamilyName('');
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-landing transition-colors touch-target"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Edit Family Form */}
        {showEditForm && family && (
          <div className="bg-white rounded-landing shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Edit Family</h2>
            <form onSubmit={handleUpdateFamily} className="space-y-4">
              <div>
                <label htmlFor="editFamilyName" className="block text-xs font-medium text-gray-700 mb-2">
                  Family Name
                </label>
                <input
                  type="text"
                  id="editFamilyName"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target min-h-[48px]"
                />
              </div>
              
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Location (for weather)</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="familyLocationCity" className="block text-xs font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      id="familyLocationCity"
                      value={familyLocationCity}
                      onChange={(e) => setFamilyLocationCity(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target min-h-[48px]"
                      placeholder="e.g., San Francisco, CA or 94103 or 37.7749,-122.4194"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter your location in any format: city name (e.g., "San Francisco, CA"), US zip code (e.g., "94103"), or coordinates (e.g., "37.7749,-122.4194")
                    </p>
                  </div>
                  
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium mb-2">
                      Advanced: Use coordinates instead
                    </summary>
                    <div className="mt-3 space-y-3 pt-3 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label htmlFor="familyLocationLat" className="block text-xs font-medium text-gray-700 mb-2">
                            Latitude
                          </label>
                          <input
                            type="number"
                            id="familyLocationLat"
                            value={familyLocationLat}
                            onChange={(e) => setFamilyLocationLat(e.target.value)}
                            min="-90"
                            max="90"
                            step="0.000001"
                            className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target min-h-[48px]"
                            placeholder="40.7128"
                          />
                        </div>
                        <div>
                          <label htmlFor="familyLocationLng" className="block text-xs font-medium text-gray-700 mb-2">
                            Longitude
                          </label>
                          <input
                            type="number"
                            id="familyLocationLng"
                            value={familyLocationLng}
                            onChange={(e) => setFamilyLocationLng(e.target.value)}
                            min="-180"
                            max="180"
                            step="0.000001"
                            className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target min-h-[48px]"
                            placeholder="-74.0060"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        If both coordinates are provided, they will be used instead of the location text above.
                      </p>
                    </div>
                  </details>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className={`flex-1 ${tokens.button.primary} text-white font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
                  disabled={isSavingFamily}
                >
                  {isSavingFamily ? <LoadingSpinner size="sm" label="Saving family" /> : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false);
                    setFamilyName(family.name);
                    setFamilyLocationCity(family.location?.city || '');
                    setFamilyLocationLat(family.location?.latitude?.toString() || '');
                    setFamilyLocationLng(family.location?.longitude?.toString() || '');
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px]"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Add Member Form */}
        {showAddMemberForm && (
          <div className="bg-white rounded-landing shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Add Family Member</h2>
            <form onSubmit={handleAddMember} className="space-y-4">
              <div>
                <label htmlFor="memberRole" className="block text-xs font-medium text-gray-700 mb-2">
                  Relationship
                </label>
                <select
                  id="memberRole"
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value as 'parent' | 'child' | 'adult')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                >
                  <option value="parent">Parent</option>
                  <option value="adult">Adult</option>
                  <option value="child">Child</option>
                </select>
              </div>
              <div>
                <label htmlFor="memberName" className="block text-xs font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="memberName"
                  value={memberName}
                  onChange={(e) => setMemberName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                  placeholder="Member name"
                />
              </div>
              <div>
                <label htmlFor="memberEmail" className="block text-xs font-medium text-gray-700 mb-2">
                  Email {memberRole === 'child' && <span className="text-gray-500 font-normal">(optional)</span>}
                </label>
                <input
                  type="email"
                  id="memberEmail"
                  value={memberEmail}
                  onChange={(e) => setMemberEmail(e.target.value)}
                  required={memberRole !== 'child'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                  placeholder="member@example.com"
                />
                {memberRole === 'child' && (
                  <p className="mt-1 text-xs text-gray-500">Children don't need an email address in kiosk mode</p>
                )}
              </div>
              <div>
                {isMobileOrTablet ? (
                  <ColorPicker
                    value={memberColor}
                    onChange={setMemberColor}
                    label="Color"
                  />
                ) : (
                  <>
                    <label htmlFor="memberColor" className="block text-sm font-medium text-gray-700 mb-2">
                      Color
                    </label>
                    <input
                      type="color"
                      id="memberColor"
                      value={memberColor}
                      onChange={(e) => setMemberColor(e.target.value)}
                      className="w-full h-12 border border-gray-300 rounded-landing cursor-pointer touch-target"
                    />
                  </>
                )}
              </div>
              <div>
                <label htmlFor="memberAvatar" className="block text-xs font-medium text-gray-700 mb-2">
                  Photo (optional)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="memberAvatar"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-landing transition-colors touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
                  >
                    {uploading ? 'Uploading...' : memberAvatar ? 'Change Photo' : 'Choose Photo'}
                  </button>
                  {memberAvatar && (
                    <div className="relative">
                      <img
                        src={memberAvatar}
                        alt="Preview"
                        className="w-16 h-16 rounded-landing-full object-cover border-2 border-gray-300"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setMemberAvatar('')}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-landing-full flex items-center justify-center text-xs font-bold hover:bg-red-600 touch-target"
                        aria-label="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500">Max file size: 5MB. Supported formats: JPEG, PNG, GIF, WebP</p>
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className={`flex-1 ${tokens.button.primary} text-white font-semibold py-3 px-6 rounded-landing transition-colors touch-target flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
                  disabled={isAddingMember}
                >
                  {isAddingMember ? <LoadingSpinner size="sm" label="Adding member" /> : 'Add Member'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddMemberForm(false);
                    setMemberEmail('');
                    setMemberName('');
                    setMemberRole('parent');
                    setMemberColor('#3b82f6');
                    setMemberAvatar('');
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-landing transition-colors touch-target"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Invite Member Form */}
        {showInviteForm && canManageFamily && (
          <div className="bg-white rounded-landing shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Invite Family Member</h2>
            <form onSubmit={handleCreateInvitation} className="space-y-4">
              <div>
                <label htmlFor="inviteEmail" className="block text-xs font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="inviteEmail"
                  value={memberEmail}
                  onChange={(e) => setMemberEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label htmlFor="inviteName" className="block text-xs font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="inviteName"
                  value={memberName}
                  onChange={(e) => setMemberName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                  placeholder="Member name"
                />
              </div>
              <div>
                <label htmlFor="inviteRole" className="block text-xs font-medium text-gray-700 mb-2">
                  Role *
                </label>
                <select
                  id="inviteRole"
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value as 'parent' | 'adult')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent touch-target"
                >
                  <option value="parent">Parent</option>
                  <option value="adult">Adult</option>
                </select>
              </div>
              <div>
                {isMobileOrTablet ? (
                  <ColorPicker
                    value={memberColor}
                    onChange={setMemberColor}
                    label="Color"
                  />
                ) : (
                  <>
                    <label htmlFor="inviteColor" className="block text-sm font-medium text-gray-700 mb-2">
                      Color
                    </label>
                    <input
                      type="color"
                      id="inviteColor"
                      value={memberColor}
                      onChange={(e) => setMemberColor(e.target.value)}
                      className="w-full h-12 border border-gray-300 rounded-landing cursor-pointer touch-target"
                    />
                  </>
                )}
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-landing transition-colors touch-target flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={isInvitingMember}
                >
                  {isInvitingMember ? <LoadingSpinner size="sm" label="Sending invitation" /> : 'Send Invitation'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteForm(false);
                    setMemberEmail('');
                    setMemberName('');
                    setMemberRole('parent');
                    setMemberColor('#3b82f6');
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-landing transition-colors touch-target"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Pending Invitations List */}
        {family && canManageFamily && invitations.length > 0 && (
          <div className="bg-white rounded-landing shadow-md p-4 sm:p-5 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Pending Invitations</h2>
            <div className="space-y-3">
              {invitations.map((invitation) => {
                const expiresAt = new Date(invitation.expiresAt * 1000);
                const daysUntilExpiry = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                
                return (
                  <div
                    key={invitation.invitationId}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-landing hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div
                        className="w-10 h-10 rounded-landing-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                        style={{ backgroundColor: invitation.color }}
                      >
                        {invitation.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 truncate">{invitation.name}</div>
                        <div className="text-sm text-gray-600 truncate">{invitation.email}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {ROLE_LABELS[invitation.role]} • Expires in {daysUntilExpiry} {daysUntilExpiry === 1 ? 'day' : 'days'}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleResendInvitation(invitation.invitationId)}
                        disabled={resendingInvitations.has(invitation.invitationId) || sentInvitations.has(invitation.invitationId)}
                        className={`px-3 py-2 font-semibold rounded-landing transition-colors touch-target ${
                          sentInvitations.has(invitation.invitationId)
                            ? 'bg-green-100 text-green-700 cursor-default'
                            : resendingInvitations.has(invitation.invitationId)
                            ? 'bg-gray-100 text-gray-500 cursor-wait'
                            : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                        }`}
                      >
                        {sentInvitations.has(invitation.invitationId)
                          ? 'Sent'
                          : resendingInvitations.has(invitation.invitationId)
                          ? 'Sending...'
                          : 'Resend'}
                      </button>
                      <button
                        onClick={() => handleCancelInvitation(invitation.invitationId)}
                        className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 font-semibold rounded-landing transition-colors touch-target"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Members List */}
        {family && (
          <div className="bg-white rounded-landing shadow-md p-4 sm:p-5">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Family Members</h2>
            <div className="space-y-3">
              {family.members.map((member) => (
                <div
                  key={member.userId}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-landing hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <div className="relative group">
                      <div
                        className="w-10 h-10 rounded-landing-full flex items-center justify-center text-white font-bold text-base relative z-0"
                        style={{ backgroundColor: member.color }}
                      >
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      {member.avatar && (
                        <img
                          src={member.avatar}
                          alt={member.name}
                          className="absolute inset-0 w-10 h-10 rounded-landing-full object-cover border-2 z-10"
                          style={{ 
                            borderColor: member.color,
                            backgroundColor: member.color
                          }}
                          key={member.avatar} // Force re-render when avatar changes
                          onError={(e) => {
                            // Hide image on error, fallback circle will show
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      {canEditMemberPhoto(member) && (
                        <>
                          {/* Always visible edit button for touch devices */}
                          <button
                            onClick={() => handleEditMemberPhoto(member.userId || '')}
                            className={`absolute -bottom-1 -right-1 w-6 h-6 ${tokens.button.primary} text-white rounded-landing-full flex items-center justify-center shadow-md transition-colors touch-target z-20`}
                            aria-label="Edit photo"
                            title="Edit photo"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          {/* Hover overlay for desktop */}
                          <div
                            className="absolute inset-0 bg-black opacity-0 group-hover:opacity-50 rounded-landing-full flex items-center justify-center transition-opacity hidden md:flex pointer-events-none z-5"
                          >
                            <span className="text-white text-xs font-semibold opacity-0 group-hover:opacity-100">Edit</span>
                          </div>
                          <button
                            onClick={() => handleEditMemberPhoto(member.userId || '')}
                            className="absolute inset-0 rounded-landing-full hidden md:flex z-5"
                            aria-label="Edit photo"
                          />
                        </>
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-gray-800">{member.name}</div>
                      {member.email && (
                        <div className="text-xs text-gray-600">{member.email}</div>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-1 rounded-landing text-xs font-medium ${ROLE_COLORS[member.role]}`}>
                          {ROLE_LABELS[member.role]}
                        </span>
                        <span className="text-xs text-gray-500">
                          Joined {new Date(member.joinedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canEditMemberPhoto(member) && (
                      <button
                        onClick={() => handleEditMember(member)}
                        className={`px-4 py-2 ${tokens.button.secondary} ${tokens.text.accent} font-semibold rounded-landing transition-colors touch-target`}
                      >
                        Edit
                      </button>
                    )}
                    {canManageFamily && member.role !== 'owner' && (
                      <button
                        onClick={() => handleRemoveMember(member.userId)}
                        className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 font-semibold rounded-landing transition-colors touch-target"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Hidden file input for editing member photos */}
        <input
          ref={editFileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Image Cropper Modal */}
        {showCropper && (
          <ImageCropper
            image={imageToCrop}
            onCrop={handleCropComplete}
            onCancel={() => {
              setShowCropper(false);
              setImageToCrop('');
              setCroppingForFamily(false);
              setShowEditMemberPhoto(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
              if (editFileInputRef.current) {
                editFileInputRef.current.value = '';
              }
              if (editMemberFileInputRef.current) {
                editMemberFileInputRef.current.value = '';
              }
              if (familyPhotoInputRef.current) {
                familyPhotoInputRef.current.value = '';
              }
            }}
            size={200}
          />
        )}

        {/* Remove Member Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showRemoveMemberConfirm}
          title="Remove Member"
          message="Are you sure you want to remove this member? This action cannot be undone."
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={handleConfirmRemoveMember}
          onCancel={handleCancelRemoveMember}
          isDestructive={true}
        />
        <ConfirmDialog
          isOpen={showSignOutConfirm}
          title="Sign Out from All Devices"
          message="This will sign you out from all devices where you're currently signed in. You'll need to sign in again on all devices. Continue?"
          confirmText="Sign Out Everywhere"
          cancelText="Cancel"
          onConfirm={async () => {
            await signOutFromAllDevices();
            navigate('/login');
            setShowSignOutConfirm(false);
          }}
          onCancel={() => setShowSignOutConfirm(false)}
          isDestructive={true}
        />
        </div>
      </div>
      <CoreNavRail active="account" />
    </div>
  );
}

